import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C';

const ArrowLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const XIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="380" y1="160" x2="680" y2="160" />
        <line x1="0" y1="420" x2="210" y2="420" /><line x1="570" y1="420" x2="880" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" />
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const statusOptions = ['all', 'pending', 'payment_verified', 'approved', 'rejected', 'completed'];
const statusConfig = {
  all:              { label: 'All',              color: 'var(--muted)',   bg: 'var(--bg3)',            border: 'var(--border)'        },
  pending:          { label: 'Pending',          color: 'var(--warning)', bg: 'rgba(245,166,35,0.12)', border: 'rgba(245,166,35,0.3)' },
  payment_verified: { label: 'Payment Verified', color: '#3b82f6',        bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' },
  approved:         { label: 'Approved',         color: 'var(--success)', bg: 'rgba(13,186,121,0.12)', border: 'rgba(13,186,121,0.3)' },
  rejected:         { label: 'Rejected',         color: 'var(--danger)',  bg: 'rgba(240,65,85,0.12)',  border: 'rgba(240,65,85,0.3)'  },
  completed:        { label: 'Completed',        color: V2,               bg: 'rgba(127,0,255,0.10)', border: 'rgba(127,0,255,0.3)'  },
};

const PAGE_SIZE = 10;

const selectStyle = {
  background: 'var(--card-solid)', border: '1.5px solid var(--border)',
  borderRadius: 9, color: 'var(--text)', padding: '8px 12px',
  fontSize: 13, fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 600,
  outline: 'none', cursor: 'pointer',
};

const AdminApplications = () => {
  const [all, setAll]           = useState([]);
  const [status, setStatus]     = useState('all');
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);

  // Extra filters (client-side)
  const [search, setSearch]     = useState('');
  const [duration, setDuration] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [sortDir, setSortDir]   = useState('desc'); // newest first

  // Fetch everything (large limit) filtered by status only
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: 1, limit: 500 });
    if (status !== 'all') params.append('status', status);
    API.get(`/admin/applications?${params}`)
      .then(r => setAll(r.data.applications || []))
      .finally(() => setLoading(false));
  }, [status]);

  // Unique durations from loaded data
  const durations = useMemo(() => {
    const s = new Set(all.map(a => a.course?.duration).filter(Boolean));
    return [...s].sort();
  }, [all]);

  // Client-side filtering + sorting
  const filtered = useMemo(() => {
    let list = [...all];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.studentName?.toLowerCase().includes(q) ||
        a.studentEmail?.toLowerCase().includes(q) ||
        a.course?.title?.toLowerCase().includes(q)
      );
    }
    if (duration) list = list.filter(a => a.course?.duration === duration);
    if (dateFrom) list = list.filter(a => new Date(a.createdAt) >= new Date(dateFrom));
    if (dateTo)   list = list.filter(a => new Date(a.createdAt) <= new Date(dateTo + 'T23:59:59'));
    list.sort((a, b) => sortDir === 'desc'
      ? new Date(b.createdAt) - new Date(a.createdAt)
      : new Date(a.createdAt) - new Date(b.createdAt)
    );
    return list;
  }, [all, search, duration, dateFrom, dateTo, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setSearch(''); setDuration(''); setDateFrom(''); setDateTo('');
    setSortDir('desc'); setPage(1);
  };
  const hasExtra = search || duration || dateFrom || dateTo || sortDir !== 'desc';

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* ══ HEADER ══ */}
      <section style={{ padding: '120px 0 60px', position: 'relative', overflow: 'hidden', background: 'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle,rgba(43,0,87,0.4) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <Link to="/admin" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 24, transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = V2}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
          >
            <ArrowLeftIcon /> Admin Dashboard
          </Link>
          <div>
            <div className="section-label">ADMIN PANEL</div>
            <h1 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', fontFamily: "'Inter',sans-serif" }}>
              All <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Applications</span>
            </h1>
            <p style={{ color: 'var(--muted)', marginTop: 8, fontSize: 14 }}>
              {filtered.length} application{filtered.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>
      </section>

      {/* ══ FILTERS + TABLE ══ */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ paddingTop: 40 }}>

          {/* ── Status pills ── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            {statusOptions.map(s => {
              const cfg = statusConfig[s];
              const isActive = status === s;
              return (
                <button key={s}
                  onClick={() => { setStatus(s); setPage(1); }}
                  style={{
                    padding: '7px 16px', borderRadius: 8, cursor: 'pointer',
                    fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 13,
                    background: isActive ? cfg.bg : 'var(--card-solid)',
                    color: isActive ? cfg.color : 'var(--muted)',
                    border: `1.5px solid ${isActive ? cfg.border : 'var(--border)'}`,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = cfg.border; e.currentTarget.style.color = cfg.color; } }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--muted)'; } }}
                >
                  {cfg.label}
                </button>
              );
            })}
          </div>

          {/* ── Advanced filters row ── */}
          <div style={{
            background: 'var(--card-solid)', border: '1px solid var(--border)',
            borderRadius: 14, padding: '16px 20px', marginBottom: 24,
            display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
          }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 180 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }}><SearchIcon /></span>
              <input
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search student or course..."
                style={{ ...selectStyle, paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            {/* Duration */}
            <select value={duration} onChange={e => { setDuration(e.target.value); setPage(1); }} style={{ ...selectStyle, flex: '0 1 160px' }}>
              <option value="">All Durations</option>
              {durations.map(d => <option key={d} value={d}>{d}</option>)}
            </select>

            {/* Date From */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>From</span>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                style={{ ...selectStyle }} />
            </div>

            {/* Date To */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>To</span>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
                style={{ ...selectStyle }} />
            </div>

            {/* Sort */}
            <select value={sortDir} onChange={e => { setSortDir(e.target.value); setPage(1); }} style={{ ...selectStyle, flex: '0 1 150px' }}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>

            {/* Clear */}
            {hasExtra && (
              <button onClick={resetFilters} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--danger)',
                background: 'transparent', color: 'var(--danger)',
                fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 12.5,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(240,65,85,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <XIcon /> Clear
              </button>
            )}
          </div>

          {/* ── Table ── */}
          <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--muted)', fontSize: 15 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${V1}33`, borderTopColor: V1, animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
                Loading...
              </div>
            ) : paginated.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)', fontSize: 15 }}>
                No applications match your filters.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
                      {['Student', 'Course', 'Duration', 'Txn ID', 'Amount', 'Status', 'Date', 'Action'].map(h => (
                        <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: 'var(--muted)', fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((app, i) => {
                      const st = statusConfig[app.status] || statusConfig.pending;
                      return (
                        <tr key={app._id}
                          style={{ borderBottom: i < paginated.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{app.studentName}</div>
                            <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>{app.studentEmail}</div>
                          </td>
                          <td style={{ padding: '14px 16px', color: 'var(--text-2)', fontSize: 13.5, maxWidth: 160 }}>
                            <div style={{ fontWeight: 600 }}>{app.course?.title}</div>
                          </td>
                          <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                            {app.course?.duration || '—'}
                          </td>
                          <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 12, fontFamily: "'Space Mono',monospace", whiteSpace: 'nowrap' }}>
                            {app.gpayTransactionId}
                          </td>
                          <td style={{ padding: '14px 16px', color: V2, fontWeight: 700, fontSize: 14 }}>₹{app.amountPaid?.toLocaleString()}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>
                              {st.label}
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                            {new Date(app.createdAt).toLocaleDateString('en-IN')}
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <Link to={`/admin/applications/${app._id}`} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '7px 14px', borderRadius: 8,
                              background: `linear-gradient(135deg,${V1},${V3})`,
                              color: '#fff', fontSize: 12.5, fontWeight: 700,
                              fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'opacity 0.2s',
                            }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                              <EyeIcon /> Review
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 28, alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ width: 36, height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--card-solid)', color: 'var(--muted)', cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: page === 1 ? 0.4 : 1, transition: 'all 0.2s' }}>
                <ChevronLeftIcon />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)} style={{
                  width: 36, height: 36, borderRadius: 8, cursor: 'pointer',
                  fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 14,
                  background: page === p ? `linear-gradient(135deg,${V1},${V3})` : 'var(--card-solid)',
                  color: page === p ? '#fff' : 'var(--muted)',
                  border: `1.5px solid ${page === p ? V1 : 'var(--border)'}`,
                  boxShadow: page === p ? `0 4px 16px ${V1}44` : 'none', transition: 'all 0.2s',
                }}>{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ width: 36, height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--card-solid)', color: 'var(--muted)', cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: page === totalPages ? 0.4 : 1, transition: 'all 0.2s' }}>
                <ChevronRightIcon />
              </button>
            </div>
          )}
        </div>
      </section>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminApplications;
